import React from 'react';

import {
  IPharmacyDetailModel,
  ServiceTypes,
  convertToDecimals,
  currency,
  fallbackText,
  representNullValue,
} from '@pharmaplan/common';
import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';

import strings from '../../../../../localization';
import PharmacyOperation from '../../../../SelfService/PharmacyOperation';

const isSelfService = ServiceTypes.self === ServiceHandler.getService();
const hourlyRateKey = 'hourlyRate';
const planKey = 'planKey';

interface IPharmacyProfileConfig {
  details: IPharmacyDetailModel;
  language: string;
  software: string;
  successAction: string;
  selectedPreferences: string;
  isAutoSyncRecord?: boolean;
}

export const handleValue = (value: string | number | null) => {
  if (!value) {
    return representNullValue;
  }

  return value;
};

export const handleBooleanValue = (value: boolean) => {
  if (!value) {
    return strings.no;
  }

  return strings.yes;
};

export const convertDecimal = (rate: number | null): string | null => {
  if (rate) {
    return `${currency}${convertToDecimals(rate)}`;
  }
  return `${currency}0`;
};

export const pharmacyProfileConfig = ({
  details,
  language,
  software,
  successAction,
  selectedPreferences,
  isAutoSyncRecord,
}: IPharmacyProfileConfig) => {
  const { pharmacy, planName } = details ?? {};
  const {
    pharmacyShortName,
    phone,
    email,
    emergencyPhone,
    administrator,
    code,
    useLSE,
    number,
    fax,
    province,
    address,
    postalCode,
    region,
    city,
    primaryContactEmail,
    primaryContactMobile,
    primaryContactName,
    primaryContactPhone,
    primaryContactPhoneExt,
    secondaryContactEmail,
    name,
    secondaryContactMobile,
    secondaryContactName,
    secondaryContactPhone,
    secondaryContactPhoneExt,
    accountingContactEmail,
    accountingContactMobile,
    accountingContactName,
    accountingContactPhone,
    accountingContactPhoneExt,
    hourlyRate,
    hourlyRateTech,
    banner,
    pharmacyHourlyRate,
    communicationEmail,
  } = pharmacy ?? {};

  const pharmacistRate = convertDecimal(hourlyRate);

  const technicianHourlyRate = convertDecimal(hourlyRateTech);
  const pharmacyRate = convertDecimal(pharmacyHourlyRate);

  const hourlyRates = [
    {
      key: 0,
      label: strings.pharmacistHourlyRate,
      value: handleValue(pharmacistRate),
    },
    {
      key: 1,
      label: strings.pharmacyHourlyRate,
      value: handleValue(pharmacyRate),
    },
    {
      key: 2,
      label: strings.technicianHourlyRate,
      value: handleValue(technicianHourlyRate),
    },
  ];

  let generalConfig = [
    {
      key: 0,
      label: strings.email,
      value: handleValue(email),
    },
    {
      key: 1,
      label: strings.ownerCompanyName,
      value: handleValue(name),
    },
    {
      key: 2,
      label: strings.shortPharmacyName,
      value: handleValue(pharmacyShortName),
      maxLength: 20,
    },
    {
      key: 3,
      label: strings.banner,
      value: handleValue(banner),
    },
    {
      key: 4,
      label: strings.bannerNumber,
      value: handleValue(number),
    },
    {
      key: 5,
      label: strings.software,
      value: handleValue(software),
    },
    {
      key: 6,
      label: strings.contactPreferences,
      value: handleValue(selectedPreferences),
    },
    {
      key: 7,
      label: strings.preferredLanguage,
      value: handleValue(language),
    },
    {
      key: 8,
      label: strings.pharmaplanID,
      value: handleValue(code),
    },
    {
      key: 9,
      label: strings.administrator,
      value: handleValue(administrator),
    },
    {
      key: planKey,
      label: strings.planType,
      value: planName ?? fallbackText,
    },
  ];

  if (!isSelfService || isAutoSyncRecord) {
    generalConfig = generalConfig.filter((item) =>
      item.key !== planKey);
  }

  let arr = [
    {
      key: 0,
      title: strings.general,
      keepExpanded: true,
      cardContent: generalConfig,
    },
    {
      key: 1,
      title: strings.coordinates,
      cardContent: [
        {
          key: 0,
          label: strings.pharmacyPhone,
          value: handleValue(phone),
        },
        {
          key: 1,
          label: strings.pharmacyFax,
          value: handleValue(fax),
        },
        {
          key: 2,
          label: strings.emergencyPhone,
          value: handleValue(emergencyPhone),
        },
        {
          key: 3,
          label: strings.address,
          value: handleValue(address),
        },
        {
          key: 4,
          label: strings.city,
          value: handleValue(city),
        },
        {
          key: 5,
          label: strings.province,
          value: handleValue(province),
        },
        {
          key: 6,
          label: strings.postalCode,
          value: handleValue(postalCode),
        },
        {
          key: 7,
          label: strings.region,
          value: handleValue(region),
        },
      ],
    },
    {
      key: 2,
      title: strings.operationalInfo,
      customComponent: (
        <PharmacyOperation
          successAction={successAction}
          title=""
          customStyle={{ padding: 0 }}
          event={pharmacy}
        />
      ),
    },
    {
      key: 3,
      title: strings.contactDetails,
      cardContent: [
        {
          key: 0,
          label: strings.primaryContactName,
          value: handleValue(primaryContactName),
        },
        {
          key: 1,
          label: strings.primaryContactPhone,
          value: handleValue(primaryContactPhone),
        },
        {
          key: 2,
          label: strings.contactPhoneExtension,
          value: handleValue(primaryContactPhoneExt),
        },
        {
          key: 3,
          label: strings.primaryContactMobile,
          value: handleValue(primaryContactMobile),
        },
        {
          key: 4,
          label: strings.primaryContactEmail,
          value: handleValue(primaryContactEmail),
        },
        {
          key: 5,
          label: strings.communicationsEmail,
          value: handleValue(communicationEmail),
        },
        {
          key: 6,
          label: strings.secondaryContactName,
          value: handleValue(secondaryContactName),
        },
        {
          key: 7,
          label: strings.secondaryContactPhone,
          value: handleValue(secondaryContactPhone),
        },
        {
          key: 8,
          label: strings.contactPhoneExtension,
          value: handleValue(secondaryContactPhoneExt),
        },
        {
          key: 9,
          label: strings.secondaryContactMobile,
          value: handleValue(secondaryContactMobile),
        },
        {
          key: 10,
          label: strings.secondaryContactEmail,
          value: handleValue(secondaryContactEmail),
        },
        {
          key: 11,
          label: strings.accountingContactName,
          value: handleValue(accountingContactName),
        },
        {
          key: 12,
          label: strings.accountingContactPhone,
          value: handleValue(accountingContactPhone),
        },
        {
          key: 13,
          label: strings.contactPhoneExtension,
          value: handleValue(accountingContactPhoneExt),
        },
        {
          key: 14,
          label: strings.accountingContactMobile,
          value: handleValue(accountingContactMobile),
        },
        {
          key: 15,
          label: strings.accountingContactEmail,
          value: handleValue(accountingContactEmail),
        },
      ],
    },
    {
      key: 4,
      title: strings.preferences,
      cardContent: [
        {
          key: 0,
          label: strings.useLabSchedulingExt,
          value: handleBooleanValue(useLSE),
        },
      ],
    },
    {
      key: hourlyRateKey,
      title: strings.hourlyRates,
      cardContent: hourlyRates,
    },
  ];

  if (isSelfService) {
    arr = arr.filter((item) =>
      item.key !== hourlyRateKey);
  }

  return arr;
};
